import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, TableContainer, Table, TableHead, TableBody, TableRow, TableCell,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, TextField,
  MenuItem, Snackbar, Alert, Grid, Card, CardContent, IconButton, Tooltip, Stack, Chip,
  CircularProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AlarmIcon from '@mui/icons-material/Alarm';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { getAllUsers } from '../../services/authService';
import {
  getLateIncidents, getLateSummary, createLateIncident, updateLateIncident, deleteLateIncident,
} from '../../services/lateIncidentService';

const fmtVnd = (n) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(n || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('vi-VN') : '-';

const SHIFT_TYPES = [
  { value: 'normal', label: 'Ca thường (10–13h / 20–23h)' },
  { value: 'sale_night', label: 'Ca sale / ca đêm (23–01h)' },
  { value: 'extra', label: 'Giờ phụ (ngoài 3 khung)' },
];
const SHIFT_SHORT = { normal: 'Ca thường', sale_night: 'Ca sale/đêm', extra: 'Giờ phụ' };

// Các trường hợp được miễn phạt theo chính sách (báo Lead trước ≥60 phút + có bằng chứng)
const EXCUSE_REASONS = [
  'Sự cố y tế / tai nạn',
  'Mất điện / mất mạng diện rộng',
  'Sự cố nền tảng Shopee / TikTok',
  'Thiết bị công ty hỏng (không do lỗi cá nhân)',
];

const currentMonthKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const emptyForm = () => ({
  userId: '', date: new Date().toISOString().slice(0, 10), shiftType: 'normal', platform: 'shopee',
  scheduledStartTime: '', actualStartTime: '', status: 'confirmed', excuseReason: '', note: '',
});

const LateIncidentManagement = ({ isMobile }) => {
  const [users, setUsers] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const [monthKey, setMonthKey] = useState(currentMonthKey());
  const [filterUserId, setFilterUserId] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [noti, setNoti] = useState({ open: false, message: '', severity: 'success' });

  const show = (message, severity = 'success') => setNoti({ open: true, message, severity });
  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Chỉ nhân sự live mới cần ghi nhận trễ
  const liveUsers = useMemo(
    () => users.filter(u => ['live_shopee', 'live_tiktok', 'live_multi', 'leader_live'].includes(u.role)),
    [users]
  );

  const fetchUsers = useCallback(async () => {
    const res = await getAllUsers();
    if (res.success) setUsers(res.data || []);
  }, []);

  const fetchIncidents = useCallback(async () => {
    setLoading(true);
    const res = await getLateIncidents({ monthKey, ...(filterUserId ? { userId: filterUserId } : {}) });
    if (res.success) setIncidents(res.data || []);
    else show(res.error?.message || 'Lỗi tải danh sách', 'error');
    setLoading(false);
  }, [monthKey, filterUserId]);

  // Tiền phạt dự kiến chỉ tính được cho MỘT người (hệ số lũy tiến theo từng người)
  const fetchSummary = useCallback(async () => {
    if (!filterUserId) { setSummary(null); return; }
    const res = await getLateSummary(filterUserId, { monthKey });
    setSummary(res.success ? res.data : null);
  }, [filterUserId, monthKey]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchIncidents(); }, [fetchIncidents]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const reload = () => { fetchIncidents(); fetchSummary(); };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm(), userId: filterUserId || '' });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      userId: row.userId?._id || row.userId || '',
      date: new Date(row.date).toISOString().slice(0, 10),
      shiftType: row.shiftType || 'normal',
      platform: row.platform || 'shopee',
      scheduledStartTime: row.scheduledStartTime || '',
      actualStartTime: row.actualStartTime || '',
      status: row.status || 'confirmed',
      excuseReason: row.excuseReason || '',
      note: row.note || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.userId) return show('Chọn nhân sự', 'error');
    if (!form.date) return show('Chọn ngày', 'error');
    if (form.status === 'excused' && !form.excuseReason) return show('Nhập lý do miễn phạt', 'error');

    setSaving(true);
    const payload = { ...form, date: new Date(form.date + 'T12:00:00').toISOString() };
    const res = editing
      ? await updateLateIncident(editing._id, payload)
      : await createLateIncident(payload);
    setSaving(false);

    if (res.success) {
      show(res.message || 'Đã lưu');
      setDialogOpen(false);
      reload();
    } else {
      show(res.error?.message || 'Lỗi lưu', 'error');
    }
  };

  const handleDelete = async () => {
    const res = await deleteLateIncident(confirmDelete._id);
    if (res.success) { show('Đã xoá'); reload(); }
    else show(res.error?.message || 'Lỗi xoá', 'error');
    setConfirmDelete(null);
  };

  const confirmedCount = incidents.filter(i => i.status !== 'excused').length;
  const excusedCount = incidents.length - confirmedCount;

  return (
    <Box>
      <Stack direction={isMobile ? 'column' : 'row'} spacing={1.5} alignItems={isMobile ? 'stretch' : 'center'} sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
          <AlarmIcon color="error" /> Chế tài đi live trễ
        </Typography>
        <TextField
          size="small" label="Tháng" type="month" InputLabelProps={{ shrink: true }}
          value={monthKey} onChange={e => setMonthKey(e.target.value)} sx={{ minWidth: 150 }}
        />
        <TextField
          size="small" select label="Nhân sự" value={filterUserId}
          onChange={e => setFilterUserId(e.target.value)} sx={{ minWidth: 200 }}
        >
          <MenuItem value="">— Tất cả —</MenuItem>
          {liveUsers.map(u => <MenuItem key={u._id} value={u._id}>{u.fullName}</MenuItem>)}
        </TextField>
        <Tooltip title="Tải lại"><span><IconButton onClick={reload} disabled={loading}><RefreshIcon /></IconButton></span></Tooltip>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none', borderRadius: 2 }}>
          Ghi nhận trễ
        </Button>
      </Stack>

      <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        Căn cứ tính trễ là giờ luồng live <b>thực tế lên sóng</b> ghi nhận trên Shopee Seller Center — không căn cứ lời khai.
        Trễ dù 1 giây cũng tính. Không lên live trong 30 phút kể từ giờ ca thì tính là <b>vắng ca</b>, không phải trễ.
      </Alert>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center">
                <EventBusyIcon color="error" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{confirmedCount}</Typography>
                  <Typography variant="caption" color="text.secondary">Lần trễ tính phạt</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h5" fontWeight={700}>{excusedCount}</Typography>
              <Typography variant="caption" color="text.secondary">Được miễn phạt</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: summary?.penalty?.amount ? '#fef2f2' : undefined }}>
            <CardContent>
              {!filterUserId ? (
                <Typography variant="caption" color="text.secondary">
                  Chọn 1 nhân sự để xem tiền phạt dự kiến (hệ số lũy tiến tính riêng từng người)
                </Typography>
              ) : !summary?.policyEnabled ? (
                <Typography variant="caption" color="text.secondary">
                  Tháng này chưa áp chế tài trễ theo chính sách
                </Typography>
              ) : (
                <Stack direction="row" spacing={1} alignItems="center">
                  <MonetizationOnIcon color="error" />
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="error.main">
                      {fmtVnd(summary?.penalty?.amount)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Phạt dự kiến — {fmtVnd(summary?.penalty?.baseAmount)} × hệ số {summary?.penalty?.multiplier ?? 1}
                    </Typography>
                  </Box>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700 }}>Ngày</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Nhân sự</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Loại ca</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Nền tảng</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Giờ ca / Lên sóng</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Trạng thái</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Thao tác</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
            ) : incidents.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">Không có lần trễ nào trong tháng này</Typography>
              </TableCell></TableRow>
            ) : incidents.map(row => (
              <TableRow key={row._id} hover>
                <TableCell>{fmtDate(row.date)}</TableCell>
                <TableCell>{row.userId?.fullName || '—'}</TableCell>
                <TableCell><Chip size="small" variant="outlined" label={SHIFT_SHORT[row.shiftType] || row.shiftType} /></TableCell>
                <TableCell sx={{ textTransform: 'capitalize' }}>{row.platform || '—'}</TableCell>
                <TableCell>
                  {row.scheduledStartTime || '—'} → <b>{row.actualStartTime || '—'}</b>
                </TableCell>
                <TableCell>
                  {row.status === 'excused' ? (
                    <Tooltip title={row.excuseReason || ''}>
                      <Chip size="small" color="success" variant="outlined" label="Miễn phạt" />
                    </Tooltip>
                  ) : (
                    <Chip size="small" color="error" label="Tính phạt" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => setConfirmDelete(row)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog thêm/sửa */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{editing ? 'Sửa lần trễ' : 'Ghi nhận lần trễ'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" select label="Nhân sự" value={form.userId} onChange={e => setField('userId', e.target.value)}>
                {liveUsers.map(u => <MenuItem key={u._id} value={u._id}>{u.fullName}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Ngày" type="date" InputLabelProps={{ shrink: true }} value={form.date} onChange={e => setField('date', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" select label="Loại ca (quyết định mức phạt)" value={form.shiftType} onChange={e => setField('shiftType', e.target.value)}>
                {SHIFT_TYPES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" select label="Nền tảng" value={form.platform} onChange={e => setField('platform', e.target.value)}>
                <MenuItem value="shopee">Shopee</MenuItem>
                <MenuItem value="tiktok">TikTok</MenuItem>
                <MenuItem value="other">Khác</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" label="Giờ ca đăng ký" type="time" InputLabelProps={{ shrink: true }} value={form.scheduledStartTime} onChange={e => setField('scheduledStartTime', e.target.value)} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Tooltip title="Giờ luồng live thực tế lên sóng trên Seller Center">
                <TextField fullWidth size="small" label="Giờ lên sóng thật" type="time" InputLabelProps={{ shrink: true }} value={form.actualStartTime} onChange={e => setField('actualStartTime', e.target.value)} />
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" select label="Trạng thái" value={form.status} onChange={e => setField('status', e.target.value)}>
                <MenuItem value="confirmed">Tính phạt</MenuItem>
                <MenuItem value="excused">Miễn phạt</MenuItem>
              </TextField>
            </Grid>
            {form.status === 'excused' && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" select label="Lý do miễn" value={form.excuseReason} onChange={e => setField('excuseReason', e.target.value)}>
                  {EXCUSE_REASONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth size="small" multiline rows={2} label="Ghi chú" value={form.note} onChange={e => setField('note', e.target.value)} placeholder="VD: trễ 4 phút, mất suất boosting phiên tối 12/08" />
            </Grid>
            {form.status === 'excused' && (
              <Grid item xs={12}>
                <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
                  Chỉ miễn phạt khi nhân sự đã báo Lead trước giờ live ít nhất 60 phút VÀ có bằng chứng.
                  Kẹt xe, ngủ quên, quên lịch, hết pin, thiết bị cá nhân lỗi đều không được miễn.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none', borderRadius: 2 }}>Huỷ</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none', borderRadius: 2 }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Lưu'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Xoá bản ghi trễ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Xoá lần trễ ngày {fmtDate(confirmDelete?.date)} của {confirmDelete?.userId?.fullName}.
            Nếu chỉ muốn không tính phạt, nên sửa trạng thái sang &ldquo;Miễn phạt&rdquo; để giữ lịch sử.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setConfirmDelete(null)} sx={{ textTransform: 'none', borderRadius: 2 }}>Huỷ</Button>
          <Button variant="contained" color="error" onClick={handleDelete} sx={{ textTransform: 'none', borderRadius: 2 }}>Xoá</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={noti.open} autoHideDuration={3000} onClose={() => setNoti(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={noti.severity} variant="filled" sx={{ borderRadius: 2 }}>{noti.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LateIncidentManagement;
